import type { IndicatorHelp } from './indicatorHelp';

/**
 * 손익 · 실현 손익 · 평가 손익 설명. (2026-09-12 사용자 요청)
 *
 * "실현손익이 표시되는 페이지들에 대해 조금 더 보충설명 해주자. 예시까지 포함해서."
 *
 * 🔴 한 곳에 둔다
 *   이 세 말은 AI 모의투자의 계좌 카드·청산 목록·매매 이력·비교 표·도움말 모달에
 *   전부 나온다. 화면마다 따로 적으면 한쪽만 고쳤을 때 같은 말을 두 가지로
 *   설명하게 된다 (CLAUDE.md 5.2절). 표 머리의 ? 도, 도움말 모달의 용어 풀이도
 *   여기서 가져다 쓴다.
 *
 * 🔴 예시 숫자는 가상이다
 *   실제 계좌 값을 박아 두면 하루만 지나도 화면과 어긋난다. 2026-09-11 저점매수(리스크)
 *   계좌의 실제 모양(실현은 +인데 평가는 −)을 본떠 반올림한 값이다.
 *
 * 🔴 왜 이 설명이 필요했나
 *   2026-09-11 실제 계좌가 이랬다 — 실현 손익 +140만인데 지금 전부 팔면 +87만.
 *   차이 −53만이 아직 안 판 종목에 들어 있었다. 실현 손익만 성적표로 읽으면
 *   그 53만을 못 본다. 그래서 "다 팔면 얼마" 를 함께 보여주고 이 글을 붙였다.
 */
export const PROFIT_HELP: IndicatorHelp = {
  label: '손익 · 실현 손익 · 평가 손익',
  hint: '판 것은 실현, 안 판 것은 평가. 둘을 합친 것이 손익',
  detail: [
    '실현 손익은 이미 판 거래에서 확정된 손익입니다. 끝난 일이라 가격이 더 움직여도 ' +
      '바뀌지 않고, 팔 때 낸 수수료와 거래세를 뺀 순액입니다.',
    '평가 손익은 아직 들고 있는 종목의 지금 손익입니다. 가격이 움직이면 같이 움직이고, ' +
      '팔기 전까지는 이익이 손실로 바뀔 수도 있습니다.',
    '손익(총 손익)은 둘을 합친 값이고, 회차 시작 금액에 그것을 더한 것이 총자산입니다. ' +
      '즉 총자산 = 회차 시작 + 실현 손익 + 평가 손익 입니다. 3회차까지는 회차 시작 금액이 곧 시드였고, ' +
      '3회차가 끝난 뒤로는 보유를 넘기므로 그 회차 첫날의 평가액(현금 + 보유)입니다.',
    // 2026-10-19 사용자 피드백 — 4회차로 넘어가며 카드 손익이 회차 시작 기준이라 3회차까지의 손익이 사라져 보였다. 숫자는 박지 않는다(카드가 API 값으로 그린다)
    '계좌 카드의 큰 숫자는 원금 대비 누적 수익률이고, 그 밑 작은 "이번 회차" 는 회차 시작 금액 대비입니다. ' +
      '원금은 계좌에 넣은 처음 돈입니다 — 3회차까지는 회차마다 원금으로 되돌려 다시 시작했으므로 마지막으로 되돌린 회차부터 셉니다. ' +
      '"지난 회차까지" 는 이번 회차가 시작될 때까지 쌓인 손익(넘어온 종목이 그때까지 오르내린 몫 포함)입니다. ' +
      '그래서 원금 + 지난 회차까지 = 회차 시작 금액이고, 여기에 이번 회차의 실현 · 평가 손익을 더하면 총자산입니다. ' +
      '원금 그대로 이번 회차를 시작한 계좌는 둘이 같아 "지난 회차까지" 와 "이번 회차" 를 따로 적지 않습니다.',
    '🔴 넘어온 종목이 있으면 계좌 칸의 평가 손익은 "이번 회차에 생긴 몫" 만 셉니다 — 지난 회차에 ' +
      '이미 오른(내린) 만큼은 회차 시작 금액에 들어 있기 때문입니다. 종목 한 줄 한 줄의 손익은 ' +
      '산 가격 기준 그대로라, 둘을 더한 값이 서로 다를 수 있습니다.',
    '예를 들어 시드 2,000만원으로 시작해 실현 손익이 +140만원, 평가 손익이 −53만원이면 ' +
      '총자산은 2,087만원이고 손익은 +87만원입니다. 실현 손익만 보면 140만원을 번 것 같지만, ' +
      '지금 전부 팔면 손에 남는 것은 +87만원입니다 — 차이 53만원이 아직 안 판 종목에 들어 있습니다.',
    '그래서 "다 팔고 그만둘까" 를 정할 때 볼 숫자는 실현 손익이 아니라 총자산(또는 "지금 다 팔면") ' +
      '하나입니다. 파는 순간 평가 손익이 전부 실현 손익으로 바뀌어 둘이 합쳐지기 때문입니다.',
    '저점매수 계좌는 오른 종목을 +3~5%에 바로 팔고 안 오른 종목은 계속 들고 있는 규칙이라, ' +
      '실현 손익은 플러스인데 평가 손익은 마이너스인 모양이 자주 나옵니다. 승률이 80%를 넘어도 ' +
      '그렇습니다 — 이기는 것을 팔고 지는 것을 들고 있는 구조가 규칙에 들어 있기 때문입니다.',
  ],
};

/**
 * 도움말 모달의 용어 풀이 한 문단. PROFIT_HELP 를 짧게 줄인 것이라 뜻이 같아야 한다.
 * 모달은 문단 하나만 받으므로 detail 을 그대로 쓰지 않고 요약본을 따로 둔다.
 */
export const PROFIT_GLOSSARY_MEANING =
  '손익은 지금 계좌 전체가 회차 시작 금액(3회차까지는 시드)보다 얼마나 늘었는지입니다 — 판 것과 아직 들고 있는 것을 ' +
  '모두 합친 값입니다. 실현 손익은 그중 실제로 팔아서 확정된 것(수수료·세금을 뺀 순액)이고, ' +
  '평가 손익은 아직 안 판 종목의 지금 손익이라 팔기 전까지 가격을 따라 움직입니다. ' +
  '총자산 = 회차 시작 + 실현 손익 + 평가 손익 입니다. ' +
  '계좌 카드의 큰 숫자(누적 수익률)는 처음 원금 대비이고 "이번 회차" 는 회차 시작 금액 대비입니다 — ' +
  '원금 + 지난 회차까지 쌓인 손익 = 회차 시작 금액입니다. ' +
  '예: 시드 2,000만 · 실현 +140만 · 평가 −53만이면 총자산 2,087만, 손익 +87만 — 실현만 보면 ' +
  '140만을 번 것 같지만 지금 다 팔면 남는 것은 +87만입니다. 다 팔지 말지는 총자산으로 판단합니다. ' +
  '2회차까지는 회차가 끝나면 보유를 전량 정리해 그때 실현 손익과 손익이 같아졌습니다. 3회차부터는 회차가 끝나도 ' +
  '보유를 넘기므로 같아지지 않고, 넘어온 종목의 평가 손익은 이번 회차에 생긴 몫만 셉니다.';
